import { DueDisplayOptions, GroupByMode, ID } from '@griply/types';
import { buildUpcomingDefaultFilter, getTimeFormat } from '@griply/utils';
import { useUpcomingExternalCalendarEvents } from 'features/external-connections';
import React, { useCallback, useMemo, useState } from 'react';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { ProvidedTaskFilterList } from 'shared/views/task-filter-list';

import { useUpcomingFilterDocument } from '../../hooks/use-upcoming-filter-document';
import { useUpcomingListCustomOrder } from '../../hooks/use-upcoming-list-custom-order';
import { useUpcomingListOnReorder } from '../../hooks/use-upcoming-list-on-reorder';

export type UpcomingGroupedListProps = {
  scrollElement: React.RefObject<HTMLDivElement>;
};

export const UpcomingGroupedList: React.FC<UpcomingGroupedListProps> = ({
  scrollElement,
}) => {
  const user = useUser();
  const today = useToday();
  const filterDocument = useUpcomingFilterDocument();

  const defaultFilterDocument = useMemo(
    () => buildUpcomingDefaultFilter(today),
    [today],
  );

  const customOrder = useUpcomingListCustomOrder();
  const onReorder = useUpcomingListOnReorder();

  const [collapsedSections, setCollapsedSections] = useState<ID[]>([]);
  const onSectionCollapse = useCallback(
    (sectionIds: ID[]) => setCollapsedSections(sectionIds),
    [],
  );

  const filterDocumentWithCollapse = useMemo(
    () => ({
      ...filterDocument,
      collapsedSections,
    }),
    [filterDocument, collapsedSections],
  );

  const externalEvents = useUpcomingExternalCalendarEvents();
  const showExternalEvents =
    !user?.settings?.upcoming?.hideExternalCalendarEvents;
  const timeFormat = getTimeFormat(user);

  const groupSettings = useMemo(
    () => ({
      [GroupByMode.UpcomingDeadline]: {
        displayDue: [DueDisplayOptions.Past, DueDisplayOptions.Future],
        externalEvents: showExternalEvents ? externalEvents : undefined,
        timeFormat,
      },
    }),
    [externalEvents, showExternalEvents, timeFormat],
  );

  return (
    <ProvidedTaskFilterList
      filterDocument={filterDocumentWithCollapse}
      defaultFilterDocument={defaultFilterDocument}
      onReorder={onReorder}
      scrollElement={scrollElement}
      customOrder={customOrder}
      onSectionCollapse={onSectionCollapse}
      excludeHiddenTodoHabits
      groupSettings={groupSettings}
      defaultNewTaskDate={today}
    />
  );
};
